import {
  type AppDefinition,
  AppPermission,
  type AppRole,
  type CustomAppGuestPermission,
  type CustomAppOwnResourcePermission,
  type CustomAppPermission,
  type CustomAppResourcePermission,
  type CustomAppResourceViewPermission,
  type CustomAppWebhookPermission,
  type PredefinedAppRole,
  predefinedAppRolePermissions,
  type Security,
} from './types/index.js';

function normalizeAppRoles(appRoles: AppRole | AppRole[] | null | undefined): AppRole[] {
  if (!appRoles) {
    return [];
  }

  return Array.isArray(appRoles) ? appRoles : [appRoles];
}

function checkAppPermissions(
  acquiredPermissions: CustomAppPermission[],
  requiredPermissions: CustomAppPermission[],
): boolean {
  const customAppResourcePermissionPattern =
    /^\$resource:[^:]+:(get|history:get|query|create|delete|patch|update)$/;

  const customAppOwnResourcePermissionPattern =
    /^\$resource:[^:]+:own:(get|query|delete|patch|update)$/;

  const customAppResourceViewPermissionPattern = /^\$resource:[^:]+:(get|query):[^:]+$/;

  const customAppWebhookPermissionPattern = /^\$webhook:[^:]+:invoke$/;

  return requiredPermissions.every((p) => {
    if (acquiredPermissions.includes(p)) {
      return true;
    }

    if (customAppResourcePermissionPattern.test(p)) {
      const [, , resourceAction, resourceExtenstion] = (p as CustomAppResourcePermission).split(
        ':',
      );
      return acquiredPermissions.includes(
        `$resource:all:${resourceAction}${resourceExtenstion ? `:${resourceExtenstion}` : ''}` as CustomAppPermission,
      );
    }

    if (customAppOwnResourcePermissionPattern.test(p)) {
      const [, resourceName, , resourceAction] = (p as CustomAppOwnResourcePermission).split(':');
      return (
        acquiredPermissions.includes(`$resource:all:${resourceAction}` as CustomAppPermission) ||
        acquiredPermissions.includes(
          `$resource:all:own:${resourceAction}` as CustomAppPermission,
        ) ||
        acquiredPermissions.includes(
          `$resource:${resourceName}:${resourceAction}` as CustomAppPermission,
        )
      );
    }

    if (customAppResourceViewPermissionPattern.test(p)) {
      const [, resourceName, resourceAction, view] = (p as CustomAppResourceViewPermission).split(
        ':',
      );

      return (
        acquiredPermissions.includes(
          `$resource:${resourceName}:${resourceAction}` as CustomAppPermission,
        ) ||
        acquiredPermissions.includes(`$resource:all:${resourceAction}` as CustomAppPermission) ||
        acquiredPermissions.includes(
          `$resource:all:${resourceAction}:${view}` as CustomAppPermission,
        )
      );
    }

    if (customAppWebhookPermissionPattern.test(p)) {
      // $webhook:all:invoke grants access to all webhooks
      // $webhook:<name>:invoke grants access to specific webhook
      const [, webhookName] = (p as CustomAppWebhookPermission).split(':');
      return (
        acquiredPermissions.includes('$webhook:all:invoke' as CustomAppPermission) ||
        (webhookName !== 'all' &&
          acquiredPermissions.includes(`$webhook:${webhookName}:invoke` as CustomAppPermission))
      );
    }
  });
}

export function getAppInheritedRoles(
  appSecurityDefinition: Security,
  roles: AppRole[],
  accumulatedRoles: AppRole[] = [],
): AppRole[] {
  if (!appSecurityDefinition || !roles) {
    return [];
  }

  for (const role of roles) {
    if (!accumulatedRoles.includes(role)) {
      accumulatedRoles.push(role);

      const roleDefinition = appSecurityDefinition.roles?.[role];
      if (roleDefinition && roleDefinition.inherits) {
        accumulatedRoles.push(
          ...getAppInheritedRoles(appSecurityDefinition, roleDefinition.inherits, accumulatedRoles),
        );
      }
    }
  }

  return Array.from(new Set(accumulatedRoles));
}

export function getAppRolePermissions(
  appSecurityDefinition: Security,
  roles: AppRole[],
): CustomAppPermission[] {
  const accumulatedPermissions: CustomAppPermission[] = [];
  const inheritedRoles = getAppInheritedRoles(appSecurityDefinition, roles);

  for (const role of inheritedRoles) {
    const roleDefinition = appSecurityDefinition.roles?.[role];

    if (roleDefinition) {
      const rolePermissions = roleDefinition.permissions;
      if (rolePermissions) {
        accumulatedPermissions.push(...rolePermissions);
      }
    } else {
      const predefinedRolePermissions = predefinedAppRolePermissions[role as PredefinedAppRole];
      if (predefinedRolePermissions) {
        accumulatedPermissions.push(...predefinedRolePermissions);
      }
    }
  }

  return Array.from(new Set(accumulatedPermissions));
}

export function getGuestAppPermissions(appSecurityDefinition: Security): CustomAppPermission[] {
  if (!appSecurityDefinition || !appSecurityDefinition.guest) {
    return [];
  }

  return [
    ...(appSecurityDefinition.guest.permissions || []),
    ...getAppRolePermissions(appSecurityDefinition, appSecurityDefinition.guest.inherits ?? []),
  ];
}

export function checkGuestAppPermissions(
  appSecurityDefinition: Security,
  requiredPermissions: CustomAppPermission[],
): boolean {
  const guestPermissions = getGuestAppPermissions(appSecurityDefinition);
  return checkAppPermissions(guestPermissions, requiredPermissions);
}

export function getAppRoles(appSecurityDefinition?: Security): AppRole[] {
  return Array.from(new Set(Object.keys(appSecurityDefinition?.roles || {})));
}

export function getAppPossibleGuestPermissions(
  appDefinition: AppDefinition,
): CustomAppGuestPermission[] {
  const possibleAllViews: Record<string, boolean> = {};

  const resourceDefinitions = Object.values(appDefinition.resources || {});
  for (const resourceDefinition of resourceDefinitions) {
    for (const view of Object.keys(resourceDefinition.views || {})) {
      possibleAllViews[view] = true;
    }
  }

  for (const view of Object.keys(possibleAllViews)) {
    if (resourceDefinitions.some((resourceDefinition) => !resourceDefinition.views?.[view])) {
      possibleAllViews[view] = false;
    }
  }

  for (const view in possibleAllViews) {
    if (!possibleAllViews[view]) {
      delete possibleAllViews[view];
    }
  }

  return [
    ...Object.values(AppPermission),
    ...Object.entries(appDefinition.resources || {}).flatMap(
      ([resourceName, resourceDefinition]) => [
        `$resource:${resourceName}:create`,
        `$resource:${resourceName}:query`,
        `$resource:${resourceName}:get`,
        `$resource:${resourceName}:history:get`,
        `$resource:${resourceName}:update`,
        `$resource:${resourceName}:patch`,
        `$resource:${resourceName}:delete`,
        ...Object.keys(resourceDefinition.views || {}).flatMap((resourceView) => [
          `$resource:${resourceName}:query:${resourceView}`,
          `$resource:${resourceName}:get:${resourceView}`,
        ]),
        ...Object.keys(possibleAllViews).flatMap((view) => [
          `$resource:all:query:${view}`,
          `$resource:all:get:${view}`,
        ]),
      ],
    ),
    ...Object.keys(appDefinition.webhooks || {}).map(
      (webhookName) => `$webhook:${webhookName}:invoke`,
    ),
  ] as CustomAppGuestPermission[];
}

export function getAppPossiblePermissions(appDefinition: AppDefinition): CustomAppPermission[] {
  return [
    ...getAppPossibleGuestPermissions(appDefinition),
    ...Object.keys(appDefinition.resources || {}).flatMap((resourceName) => [
      `$resource:${resourceName}:own:query`,
      `$resource:${resourceName}:own:get`,
      `$resource:${resourceName}:own:update`,
      `$resource:${resourceName}:own:patch`,
      `$resource:${resourceName}:own:delete`,
    ]),
  ] as CustomAppPermission[];
}

export function getAppRolesByPermissions(
  appSecurityDefinition: Security,
  requiredPermissions: CustomAppPermission[],
): (AppRole | 'Guest')[] {
  const roles = getAppRoles(appSecurityDefinition);

  const compliantRoles: AppRole[] = [];
  for (const role of roles) {
    if (
      checkAppPermissions(getAppRolePermissions(appSecurityDefinition, [role]), requiredPermissions)
    ) {
      compliantRoles.push(role);
    }
  }

  if (checkAppPermissions(getGuestAppPermissions(appSecurityDefinition), requiredPermissions)) {
    compliantRoles.push('Guest');
  }

  return compliantRoles;
}

export function checkAppRoleAppPermissions(
  appSecurityDefinition: Security,
  appRoles: AppRole | AppRole[] | null | undefined,
  requiredPermissions: CustomAppPermission[],
): boolean {
  const appRolePermissions = getAppRolePermissions(
    appSecurityDefinition,
    normalizeAppRoles(appRoles),
  );
  return checkAppPermissions(appRolePermissions, requiredPermissions);
}
